// The tab's one connection (02-DESIGN/03-console/01-the-console.md § no
// backend: one websocket connection). A tab holds at most one placed
// connection for its whole life: a page, a remount or a re-render asking for
// the connection it already holds gets that one, never a second; the
// library's reconnect is the only connect after the first. The connection
// changes only when what it serves changes — another workspace, the identity
// plane, another lane — or when the person asks again after a refusal, and
// then the one it held is closed first. A sign-in that placed the person
// directly in their one workspace is that workspace's connection already, so
// entering it adopts it rather than opening another. A connection that asked
// for the identity plane (decision 0072) serves only an ask for the plane, and
// one that did not serves only a sign-in that names no workspace.

import type { State } from "./bus.ts";
import type { PlatformProfile, TenantProfile } from "./profile.ts";

/** Lane is the credential the session carries. */
export type Lane = "oidc" | "github";

/** Holdable is what the tab needs of a connection: the Bus, or a stand-in counting connects in tests. */
export interface Holdable {
  readonly origin: string;
  readonly platform: PlatformProfile;
  readonly tenant: TenantProfile | null;
  readonly lane: Lane;
  /** Whether the connection asked for the identity plane by naming `-` as the tenant; absent is no. */
  readonly plane?: boolean;
  connect(): Promise<unknown>;
  close(): Promise<void>;
  adopt(tenant: TenantProfile): boolean;
  current(): State;
}

/** Make builds a connection that has not connected yet. */
export type Make<B extends Holdable> = (
  origin: string,
  platform: PlatformProfile,
  tenant: TenantProfile | null,
  lane: Lane,
  plane: boolean,
) => B;

/** Tab holds the one connection of a browser tab. */
export class Tab<B extends Holdable> {
  private held: B | undefined;

  constructor(private readonly make: Make<B>) {}

  /** current is the connection held now, if any. */
  current(): B | undefined {
    return this.held;
  }

  /**
   * hold answers the tab's connection for a workspace (or the identity plane
   * when null) on a lane: the one held when it serves that, the held one
   * adopted when the sign-in placed it directly in that workspace, and
   * otherwise a new one — connected once — after closing the one held.
   */
  hold(
    origin: string,
    platform: PlatformProfile,
    tenant: TenantProfile | null,
    lane: Lane,
    plane = false,
  ): B {
    const held = this.held;
    if (held !== undefined && held.lane === lane) {
      if (serves(held, tenant, plane)) {
        return held;
      }
      if (tenant !== null && held.tenant === null && held.adopt(tenant)) {
        return held;
      }
    }
    return this.open(origin, platform, tenant, lane, tenant === null && plane);
  }

  /** again closes the connection held and opens a fresh one for the same workspace: the person's *Try again*. */
  again(): B | undefined {
    const held = this.held;
    if (held === undefined) {
      return undefined;
    }
    return this.open(held.origin, held.platform, held.tenant, held.lane, held.plane ?? false);
  }

  /** release closes the connection held: signing out. */
  release(): void {
    const held = this.held;
    this.held = undefined;
    void held?.close();
  }

  private open(
    origin: string,
    platform: PlatformProfile,
    tenant: TenantProfile | null,
    lane: Lane,
    plane: boolean,
  ): B {
    this.release();
    const bus = this.make(origin, platform, tenant, lane, plane);
    this.held = bus;
    bus.connect().catch(() => undefined);
    return bus;
  }
}

/**
 * serves reports whether a connection is the one for the workspace — the same
 * slug and account — or, with no workspace, for the same ask: the plane asked
 * for, or a sign-in that names none.
 */
function serves(held: Holdable, tenant: TenantProfile | null, plane: boolean): boolean {
  if (tenant === null || held.tenant === null) {
    return tenant === held.tenant && (tenant !== null || (held.plane ?? false) === plane);
  }
  return held.tenant.slug === tenant.slug && held.tenant.account === tenant.account;
}
